import React, { useState, useRef } from 'react';
import SafeIcon from '../common/SafeIcon';
import { 
  FiMonitor, FiShield, FiAward, FiZap, FiCpu, FiUsers, FiArrowRight, FiCheck, 
  FiTerminal, FiDatabase, FiLayers, FiMail, FiMessageSquare, FiBook, FiBriefcase, 
  FiRefreshCw, FiUserPlus, FiVolume2, FiVolumeX, FiPlay, FiPause, FiExternalLink, 
  FiDownload, FiBookOpen 
} from 'react-icons/fi';
import { motion } from 'framer-motion';

const LandingPage = ({ onGetStarted, onLogin, onShowLegal, onShowAcademy, siteName, supportEmail }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef(null);

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const togglePlay = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const features = [
    { title: "Interactive Simulations", desc: "Real-world incident response scenarios ranging from VIP hardware triage to complex cloud infrastructure failures.", icon: FiTerminal, color: "text-blue-500", bg: "bg-blue-500/10" },
    { title: "Tiered Certifications", desc: "Earn official proof of competency with Tier 1 and Tier 2 certifications validated by our simulation engine.", icon: FiAward, color: "text-emerald-500", bg: "bg-emerald-500/10" },
    { title: "Performance Analytics", desc: "Track resolution accuracy, SLA compliance, and XP growth with detailed analyst metrics.", icon: FiZap, color: "text-orange-500", bg: "bg-orange-500/10" },
    { title: "Enterprise Knowledge", desc: "Full access to a centralized Standard Operating Procedure (SOP) database and technical troubleshooting guides.", icon: FiDatabase, color: "text-purple-500", bg: "bg-purple-500/10" }
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="https://media-manager-c.questera.ai/greta-media/c4cd7048ee42a7b0632cdf5aa1fa307c48d20f8ae20304e7af2c62d9e8bb8b361540d414afb44516657958e65ec582f9/images/aW1hZ2UvcG5n/ddfb2c5aeb3b376e9ab5a19c747ef4da.png" alt={siteName} className="h-12 md:h-16 object-contain" />
          </div>
          <div className="flex items-center space-x-8">
            <button onClick={onLogin} className="text-sm font-bold text-slate-600 hover:text-blue-600 transition-colors">Login</button>
            <button onClick={onGetStarted} className="bg-slate-900 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-600 transition-all shadow-lg shadow-slate-200">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-48 pb-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-blue-600 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
              </span>
              <span>Next-Gen Analyst Training</span>
            </div>
            <h1 className="text-6xl lg:text-7xl font-black text-slate-900 leading-[1.05] tracking-tight mb-8">
              Master the Art of <span className="text-blue-600">Technical Support.</span>
            </h1>
            <p className="text-lg text-slate-500 leading-relaxed max-w-lg mb-6">
              The industry-standard operating system for training world-class support analysts. Simulate real incidents, earn certifications, and track performance in real-time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={onGetStarted} className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-blue-700 transition-all shadow-2xl shadow-blue-200 flex items-center justify-center group">
                <span>Initialize Training</span>
                <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative">
            <div className="bg-slate-900 rounded-[2.5rem] p-3 shadow-3xl border border-slate-800 overflow-hidden relative group/player">
              <div className="bg-slate-950 rounded-[2rem] overflow-hidden border border-slate-800 aspect-video relative group">
                <video 
                  ref={videoRef}
                  className="w-full h-full object-cover rounded-[1.8rem] cursor-pointer"
                  autoPlay 
                  muted={isMuted} 
                  loop 
                  playsInline
                  onClick={togglePlay}
                  poster="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=1200"
                >
                  <source src="https://websitebrokers.org/videos/promovideonetdeskpro.mp4" type="video/mp4" />
                </video>
                <div className="absolute bottom-6 right-6 z-20 flex items-center space-x-3 opacity-0 group-hover/player:opacity-100 transition-all duration-300">
                  <button onClick={togglePlay} className="bg-black/40 hover:bg-black/60 backdrop-blur-md text-white p-3 rounded-full border border-white/20 transition-all">
                    <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="text-xl" />
                  </button>
                  <button onClick={toggleMute} className="bg-black/40 hover:bg-black/60 backdrop-blur-md text-white p-3 rounded-full border border-white/20 transition-all">
                    <SafeIcon icon={isMuted ? FiVolumeX : FiVolume2} className="text-xl" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Designed for the Next Generation of IT */}
      <section className="py-24 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 max-w-3xl">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">Designed for the Next Generation of IT</h2>
            <p className="text-lg text-slate-400">Whether you're starting from scratch or scaling an enterprise team, NetDesk Pro provides the environment to grow.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            <div className="bg-slate-800/30 border border-slate-700/50 p-6 rounded-2xl hover:bg-slate-800 transition-colors">
              <div className="w-10 h-10 bg-blue-500/20 text-blue-400 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiUserPlus} />
              </div>
              <h3 className="text-sm font-black text-white uppercase tracking-widest mb-3">Aspiring Techs</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Build the confidence and muscle memory needed to land your first Help Desk role.</p>
            </div>
            <div className="bg-slate-800/30 border border-slate-700/50 p-6 rounded-2xl hover:bg-slate-800 transition-colors">
              <div className="w-10 h-10 bg-blue-500/20 text-blue-400 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiMonitor} />
              </div>
              <h3 className="text-sm font-black text-white uppercase tracking-widest mb-3">IT Students</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Bridge the gap between theoretical certifications and real-world hands-on troubleshooting.</p>
            </div>
            <div className="bg-slate-800/30 border border-slate-700/50 p-6 rounded-2xl hover:bg-slate-800 transition-colors">
              <div className="w-10 h-10 bg-blue-500/20 text-blue-400 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiLayers} />
              </div>
              <h3 className="text-sm font-black text-white uppercase tracking-widest mb-3">MSP Trainees</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Standardize your onboarding process and master complex client environments quickly.</p>
            </div>
            <div className="bg-slate-800/30 border border-slate-700/50 p-6 rounded-2xl hover:bg-slate-800 transition-colors">
              <div className="w-10 h-10 bg-blue-500/20 text-blue-400 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiUsers} />
              </div>
              <h3 className="text-sm font-black text-white uppercase tracking-widest mb-3">Internal Teams</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Upskill your existing L1 staff to handle L2 escalations and reduce ticket resolution times.</p>
            </div>
            <div className="bg-slate-800/30 border border-slate-700/50 p-6 rounded-2xl hover:bg-slate-800 transition-colors">
              <div className="w-10 h-10 bg-blue-500/20 text-blue-400 rounded-lg flex items-center justify-center mb-6">
                <SafeIcon icon={FiRefreshCw} />
              </div>
              <h3 className="text-sm font-black text-white uppercase tracking-widest mb-3">Career Changers</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Fast-track your transition into IT with a simulator that counts as real-world experience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <h2 className="text-4xl font-black text-slate-900 mb-4">Built for Modern Teams</h2>
            <p className="text-slate-500 font-medium">The most comprehensive suite of tools for developing technical talent in the enterprise.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((f, i) => (
              <motion.div key={i} whileHover={{ y: -10 }} className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm">
                <div className={`w-14 h-14 ${f.bg} ${f.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <SafeIcon icon={f.icon} className="text-2xl" />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-3">{f.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* eBook Promo Section */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          {/* Main Banner */}
          <div className="bg-slate-900 rounded-[3rem] overflow-hidden shadow-2xl border border-slate-800 mb-16">
            <div className="grid lg:grid-cols-2 items-center">
              <div className="p-12 lg:p-20 space-y-8">
                <div className="inline-flex items-center space-x-2 bg-blue-500/20 text-blue-400 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest">
                  <SafeIcon icon={FiBookOpen} />
                  <span>Free eBook</span>
                </div>
                <h2 className="text-4xl lg:text-5xl font-black text-white leading-tight">
                  NetDesk Pro Playbook
                </h2>
                <p className="text-lg text-slate-400 leading-relaxed">
                  A real-world playbook for beginners entering IT support, covering ticket handling, troubleshooting, escalation, documentation, and customer service.
                </p>
                <ul className="space-y-4 text-slate-300 font-medium">
                  <li className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheck} className="text-emerald-400" />
                    <span>Practical troubleshooting frameworks</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheck} className="text-emerald-400" />
                    <span>Real-world escalation protocols</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheck} className="text-emerald-400" />
                    <span>Essential customer service skills</span>
                  </li>
                </ul>
                <div className="pt-4">
                  <button onClick={onLogin} className="bg-blue-600 text-white px-8 py-4 rounded-xl font-black uppercase tracking-widest text-sm hover:bg-blue-500 transition-all shadow-lg flex items-center space-x-2">
                    <SafeIcon icon={FiDownload} />
                    <span>Get the Playbook</span>
                  </button>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-4">
                    Member Account required to access this resource
                  </p>
                </div>
              </div>
              <div className="p-12 lg:p-20 flex justify-center bg-slate-800/50 h-full items-center">
                <img
                  src="https://media-manager-c.questera.ai/greta-media/c4cd7048ee42a7b0632cdf5aa1fa307c48d20f8ae20304e7af2c62d9e8bb8b361540d414afb44516657958e65ec582f9/images/aW1hZ2UvcG5n/b78ca2700d6a4bc2bb219fa8d251c8e3.png"
                  alt="Inside the NetDesk Pro Playbook"
                  className="w-full max-w-md rounded-xl shadow-2xl transform -rotate-2 hover:rotate-0 transition-transform duration-500"
                />
              </div>
            </div>
          </div>

          {/* Sneak Peek Gallery */}
          <div className="space-y-12">
            <div className="text-center max-w-2xl mx-auto">
              <h3 className="text-3xl font-black text-slate-900 mb-4">Look Inside the Playbook</h3>
              <p className="text-slate-500 font-medium">Stop guessing. We've compiled the exact frameworks, communication templates, and prioritization matrices used by senior analysts.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div whileHover={{ y: -5 }} className="rounded-[2rem] overflow-hidden shadow-xl border border-slate-200">
                <img src="https://media-manager-c.questera.ai/greta-media/c4cd7048ee42a7b0632cdf5aa1fa307c48d20f8ae20304e7af2c62d9e8bb8b361540d414afb44516657958e65ec582f9/images/aW1hZ2UvcG5n/4adce401c25b8d01b3c40f92f7cbd416.png" alt="What Great Support Sounds Like" className="w-full h-auto object-cover" />
              </motion.div>
              <motion.div whileHover={{ y: -5 }} className="rounded-[2rem] overflow-hidden shadow-xl border border-slate-200">
                <img src="https://media-manager-c.questera.ai/greta-media/c4cd7048ee42a7b0632cdf5aa1fa307c48d20f8ae20304e7af2c62d9e8bb8b361540d414afb44516657958e65ec582f9/images/aW1hZ2UvcG5n/3372c1f5a1ba69b8fc178dc400ef220e.png" alt="Learn to Read a Ticket" className="w-full h-auto object-cover" />
              </motion.div>
              <motion.div whileHover={{ y: -5 }} className="rounded-[2rem] overflow-hidden shadow-xl border border-slate-200">
                <img src="https://media-manager-c.questera.ai/greta-media/c4cd7048ee42a7b0632cdf5aa1fa307c48d20f8ae20304e7af2c62d9e8bb8b361540d414afb44516657958e65ec582f9/images/aW1hZ2UvcG5n/6383babc15938e76d7285b5f89772f85.png" alt="Follow a Real Troubleshooting Process" className="w-full h-auto object-cover" />
              </motion.div>
              <motion.div whileHover={{ y: -5 }} className="rounded-[2rem] overflow-hidden shadow-xl border border-slate-200">
                <img src="https://media-manager-c.questera.ai/greta-media/c4cd7048ee42a7b0632cdf5aa1fa307c48d20f8ae20304e7af2c62d9e8bb8b361540d414afb44516657958e65ec582f9/images/aW1hZ2UvcG5n/2a0aebd59c7e973bc55d6c8e1f86391c.png" alt="Prioritize Like a Real Analyst" className="w-full h-auto object-cover" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-slate-900 rounded-[3rem] p-12 md:p-20 text-center shadow-2xl border border-slate-800">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-12 leading-tight">Ready to elevate your<br/>support standards?</h2>
            
            <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-12">
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 flex items-center space-x-4 text-left">
                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                  <SafeIcon icon={FiCheck} className="text-white text-xs" />
                </div>
                <span className="text-slate-300 font-bold text-sm">Reduce Onboarding Time by 40%</span>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 flex items-center space-x-4 text-left">
                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                  <SafeIcon icon={FiCheck} className="text-white text-xs" />
                </div>
                <span className="text-slate-300 font-bold text-sm">Standardize Global Support SOPs</span>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 flex items-center space-x-4 text-left">
                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                  <SafeIcon icon={FiCheck} className="text-white text-xs" />
                </div>
                <span className="text-slate-300 font-bold text-sm">Identify High-Potential Analysts</span>
              </div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4 flex items-center space-x-4 text-left">
                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                  <SafeIcon icon={FiCheck} className="text-white text-xs" />
                </div>
                <span className="text-slate-300 font-bold text-sm">Zero-Risk Training Environment</span>
              </div>
            </div>

            <button onClick={onGetStarted} className="bg-white text-slate-900 px-10 py-5 rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-slate-100 transition-all shadow-lg hover:scale-105 transform duration-200">
              Start Free Session
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 border-t border-slate-100 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <img src="https://media-manager-c.questera.ai/greta-media/c4cd7048ee42a7b0632cdf5aa1fa307c48d20f8ae20304e7af2c62d9e8bb8b361540d414afb44516657958e65ec582f9/images/aW1hZ2UvcG5n/ddfb2c5aeb3b376e9ab5a19c747ef4da.png" alt={siteName} className="h-12 object-contain" />
              </div>
              <p className="text-slate-500 text-sm leading-relaxed">
                The world's leading simulation environment for technical support training and certification.
              </p>
            </div>
            
            <div>
              <h4 className="font-black text-xs uppercase tracking-widest text-slate-900 mb-6">Academy</h4>
              <ul className="space-y-4">
                <li><button onClick={() => onShowAcademy('simulation')} className="text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors">Simulation Engine</button></li>
                <li><button onClick={() => onShowAcademy('certification')} className="text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors">Certification Queue</button></li>
                <li><button onClick={() => onShowAcademy('kb')} className="text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors">Knowledge Base</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-black text-xs uppercase tracking-widest text-slate-900 mb-6">Legal</h4>
              <ul className="space-y-4">
                <li><button onClick={() => onShowLegal('privacy')} className="text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => onShowLegal('terms')} className="text-sm font-bold text-slate-500 hover:text-blue-600 transition-colors">Terms of Service</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-black text-xs uppercase tracking-widest text-slate-900 mb-6">Support</h4>
              <ul className="space-y-4">
                <li className="flex items-center space-x-3 text-slate-500">
                  <SafeIcon icon={FiMail} className="text-blue-600" />
                  <span className="text-sm font-bold">{supportEmail}</span>
                </li>
                <li className="flex items-center space-x-3 text-slate-500">
                  <SafeIcon icon={FiMessageSquare} className="text-blue-600" />
                  <span className="text-sm font-bold">Help Desk 24/7</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-8 pt-8 border-t border-slate-200">
            <div className="text-slate-400 text-[10px] font-black uppercase tracking-widest text-center md:text-left">
              © 2026 {siteName}. All Rights Reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;